import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTablePFSEvent } from "primereact/datatable";
import { TreeTable, TreeTablePageParams } from "primereact/treetable";
import { useCallback, useMemo, useState } from "react";
import { DCSQueryClient } from "../../../queryClient";
import { If } from "../../../components/If/If";
import { UserRoles } from "../../../constants/userRoles";
import { useFilterQuery } from "../../../hooks/useFilterQuery";
import { useToast } from "../../../Providers/ToastProvider";
import { PrivateAccess } from "../../../keycloak";
import { requests } from "../../../services/http-common";
import { IFilter } from "../../../types";
import { DeleteItemPopup } from "../components/DeleteItemPopup/DeleteItemPopup";
import { SetItemPopup } from "../components/SetItemPopup/SetItemPopup";
import { TItem } from "../components/types";
import classes from "./tabs.module.scss";
import { useEffectWithoutMount } from "../../../helpers/helperHooks";

const defaultFilters = {
  first: 0,
  rows: 10,
  page: 1,
  filter: "",
  filterByStatus: "",
  filterByCustomerCategory: "",
  sortOrder: -1,
};

export const ProjectTypes = () => {
  const [filters, setFilters] = useState<IFilter>(defaultFilters as IFilter);
  const [item, setItem] = useState<TItem | null>(null);
  const [itemDialog, setItemDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState(false);
  const filterQuery = useFilterQuery(filters);
  const { show } = useToast();

  const { data, refetch } = useQuery({
    queryKey: ["project-type-options"],
    queryFn: () => {
      return requests.get(`/contract-type?${filterQuery}`);
    },
  });

  const scopeOfWorkQueries = useQueries({
    queries: (data?.result || []).map((contractType: any) => ({
      queryKey: ["contract-type-scope-of-work-options", contractType.id],
      queryFn: () =>
        requests.get(`/scope-of-work?rows=1000&contractTypeId=${contractType.id}`),
      enabled: !!contractType.id,
    })),
  });

  const addItem = useMutation({
    mutationFn: ({
      name,
      contractTypeId,
      urlEndpoint = "contract-type",
    }: {
      name: string;
      contractTypeId?: string;
      urlEndpoint?: string;
    }) => {
      return requests.post(`/${urlEndpoint}`, { name, contractTypeId });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item added successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["project-type-options"],
      });
      DCSQueryClient.invalidateQueries({
        queryKey: ["contract-type-scope-of-work-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const editItemRequest = useMutation({
    mutationFn: ({
      name,
      id,
      contractTypeId,
      urlEndpoint = "contract-type",
    }: TItem & { urlEndpoint?: string }) => {
      return requests.put(`/${urlEndpoint}/${id}`, { name, contractTypeId });
    },
    onSuccess: () => {
      hideItemDialog();
      show({ detail: "Item updated successfully !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["project-type-options"],
      });
      DCSQueryClient.invalidateQueries({
        queryKey: ["contract-type-scope-of-work-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const uploadSowTemplate = useMutation({
    mutationFn: ({
      contractTypeId,
      sowTemplateFile,
    }: {
      contractTypeId: string;
      sowTemplateFile: File;
    }) => {
      const formData = new FormData();
      formData.append("file", sowTemplateFile);
      return requests.post(
        `/contract-type/${contractTypeId}/sow-template`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          transformRequest: (formData: any) => formData,
        }
      );
    },
    onSuccess: () => {
      show({ detail: "Scope of work template uploaded !" });
      DCSQueryClient.invalidateQueries({
        queryKey: ["project-type-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const deleteItem = useMutation({
    mutationFn: ({
      id,
      contractTypeId,
      urlEndpoint = "contract-type",
    }: TItem & { urlEndpoint?: string }) => {
      const query = contractTypeId ? `?contractTypeId=${contractTypeId}` : "";
      return requests.delete(`/${urlEndpoint}/${id}${query}`);
    },
    onSuccess: () => {
      setDeleteItemDialog(false);
      DCSQueryClient.invalidateQueries({
        queryKey: ["project-type-options"],
      });
      DCSQueryClient.invalidateQueries({
        queryKey: ["contract-type-scope-of-work-options"],
      });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const handleSetItem = (item: {
    name: string;
    id: string | null;
    projectTypeId: string;
    contractTypeId?: string;
    sowTemplateFile?: File | null;
  }) => {
    const contractTypeId = item.contractTypeId || item.projectTypeId;
    const { sowTemplateFile } = item;

    if (item.id && contractTypeId) {
      if (item.id === "contractType") {
        addItem.mutate({
          name: item.name,
          urlEndpoint: "scope-of-work",
          contractTypeId,
        });
      } else {
        editItemRequest.mutate({
          id: item.id as string,
          name: item.name,
          urlEndpoint: "scope-of-work",
          contractTypeId,
        });
      }
      return;
    }

    if (item.id && item.id !== "contractType") {
      editItemRequest.mutate(item as TItem, {
        onSuccess: () => {
          if (sowTemplateFile && item.id) {
            uploadSowTemplate.mutate({
              contractTypeId: item.id,
              sowTemplateFile,
            });
          }
        },
      });
      return;
    }

    addItem.mutate(
      { name: item.name },
      {
        onSuccess: (data: any) => {
          if (!sowTemplateFile) return;
          if (data?.id) {
            uploadSowTemplate.mutate({
              contractTypeId: data.id,
              sowTemplateFile,
            });
          } else {
            show({
              severity: "error",
              detail:
                "Project type created, but the scope of work template could not be uploaded.",
            });
          }
        },
      }
    );
  };

  const onPage = useCallback(
    (event: DataTablePFSEvent | TreeTablePageParams) => {
      setFilters((currVal: any) => ({
        ...currVal,
        first: event.first,
        rows: event.rows,
        page: event.page,
      }));
    },
    [setFilters]
  );

  useEffectWithoutMount(() => {
    refetch();
  }, [filters, refetch]);

  const editItem = (row: any) => {
    setItem(row.data);
    setItemDialog(true);
  };

  const confirmDeleteItem = (row: any) => {
    setItem(row.data);
    setDeleteItemDialog(true);
  };

  const hideDeleteItemDialog = () => {
    setDeleteItemDialog(false);
  };

  const hideItemDialog = () => {
    setItemDialog(false);
    setItem(null);
  };

  const handleAddItem = () => {
    setItem(null);
    setItemDialog(true);
  };

  const handleAddScopeOfWork = () => {
    setItem({
      name: "",
      id: "contractType",
    });
    setItemDialog(true);
  };

  const actionBodyTemplate = (rowData: any) => {
    // Child rows are scope of work records; the backend checks SOW roles for them.
    const isScopeOfWork = !!rowData.data?.contractTypeId;
    const updateRole = isScopeOfWork
      ? UserRoles.SCOPE_OF_WORK_UPDATE
      : UserRoles.PROJECT_TYPE_UPDATE;
    const deleteRole = isScopeOfWork
      ? UserRoles.SCOPE_OF_WORK_DELETE
      : UserRoles.PROJECT_TYPE_DELETE;
    return (
      <div className={classes.tableActions}>
        <PrivateAccess
          roles={{
            dcs_client: [updateRole],
          }}
        >
          <Button
            icon="pi pi-pencil"
            className="p-button-primary mr-2 p-button-sm"
            onClick={() => editItem(rowData)}
          />
        </PrivateAccess>
        <PrivateAccess
          roles={{
            dcs_client: [deleteRole],
          }}
        >
          <Button
            icon="pi pi-trash"
            className="p-button-danger p-button-sm"
            onClick={() => confirmDeleteItem(rowData)}
          />
        </PrivateAccess>
      </div>
    );
  };

  const cardTitle = () => {
    return (
      <div className={classes.cardTitle}>
        <h4>Project type and Scope of work</h4>
        <div className={classes.cardActions}>
          <PrivateAccess
            roles={{
              dcs_client: [UserRoles.PROJECT_TYPE_CREATE],
            }}
          >
            <Button
              icon="pi pi-plus"
              className=""
              label="New project type"
              onClick={handleAddItem}
            />
          </PrivateAccess>
          <PrivateAccess
            roles={{
              dcs_client: [UserRoles.SCOPE_OF_WORK_CREATE],
            }}
          >
            <Button
              icon="pi pi-plus"
              className=""
              label="New scope of work"
              onClick={handleAddScopeOfWork}
            />
          </PrivateAccess>
        </div>
      </div>
    );
  };

  const handleConfirm = (item: TItem) => {
    if (item.contractTypeId) {
      deleteItem.mutate({
        ...item,
        urlEndpoint: "scope-of-work",
      });
      return;
    }
    deleteItem.mutate(item);
  };

  const projectTypesData = useMemo(() => {
    const options: any = [];
    const datatree: any = [];
    if (data?.result) {
      data.result.forEach((contractType: any, index: number) => {
        const scopeOfWorks =
          (scopeOfWorkQueries[index]?.data as any)?.result || [];
        options.push({ label: contractType.name, value: contractType.id });
        datatree.push({
          key: contractType.id,
          data: {
            name: contractType.name,
            id: contractType.id,
            hasSowTemplate: contractType.hasSowTemplate,
          },
          children: scopeOfWorks.map(
            (scopeOfWork: any, s_index: number) => ({
              key: `${contractType.id}-${scopeOfWork.id}`,
              data: {
                name: scopeOfWork.name,
                contractTypeId: contractType.id,
                id: scopeOfWork.id,
              },
            })
          ),
        });
      });
    }

    return {
      options,
      datatree,
    };
  }, [data, scopeOfWorkQueries]);

  return (
    <div className={classes.container}>
      <Card title={cardTitle}>
        <TreeTable
          value={projectTypesData.datatree}
          paginator={true}
          paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
          first={filters.first}
          rows={filters.rows}
          totalRecords={data?.totalRecordsCount}
          onPage={onPage}
          paginatorPosition="top"
          paginatorClassName={classes.paginator}
        >
          <Column field="name" header="Name" expander></Column>
          <Column
            style={{ width: "150px" }}
            body={actionBodyTemplate}
            header="Actions"
          ></Column>
        </TreeTable>
      </Card>
      <If condition={deleteItemDialog}>
        <DeleteItemPopup
          item={item}
          onHide={hideDeleteItemDialog}
          onConfirm={handleConfirm}
          deleteRequestPending={deleteItem.isLoading}
        />
      </If>
      <If condition={itemDialog}>
        <SetItemPopup
          item={item}
          onHide={hideItemDialog}
          onSubmit={handleSetItem}
          projectTypeOptions={projectTypesData.options}
          showSowTemplate
        />
      </If>
    </div>
  );
};
